import React, { useEffect, useLayoutEffect, useState } from 'react';
import { ArrowRight, ArrowLeft, X } from 'lucide-react';

// Bump this when steps below change — it's what decides whether a user (who
// already clicked through a previous tour) sees it again. Keyed per-browser
// via localStorage, same convention as other one-time-per-release UI here.
const TOUR_VERSION = '2026-08-05';
const STORAGE_KEY = 'guidedTourSeenVersion';

interface TourStep {
  selector?: string; // omit for a centered, non-spotlit intro/outro step
  title: string;
  description: string;
}

const ALL_STEPS: TourStep[] = [
  {
    title: "What's new around here",
    description: "A quick tour of what's been added recently. Use Next/Back to move around, or skip any time.",
  },
  {
    selector: '[data-tour="sidebar-my-dashboard"]',
    title: 'My Dashboard',
    description: "Your own personal snapshot — what you've deployed, what changed, and what still needs your attention.",
  },
  {
    selector: '[data-tour="header-profile"]',
    title: 'Profile & security',
    description: 'Upload a profile picture, track how complete your profile is, and turn on two-factor authentication.',
  },
  {
    selector: '[data-tour="sidebar-audit-log"]',
    title: 'Audit Log',
    description: 'A full organization-wide history of who did what, and when.',
  },
  {
    selector: '[data-tour="sidebar-chat"]',
    title: 'Chat',
    description: 'Talk to your team without leaving the app.',
  },
  {
    title: 'Install this as an app',
    description: 'Look for the install icon in your browser\'s address bar (or the ⋮ menu → "Install..."). Once installed, you can pin it to your taskbar like Slack.',
  },
];

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const PADDING = 8;

const GuidedTour: React.FC = () => {
  const [steps, setSteps] = useState<TourStep[] | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);

  // Build the step list once, on mount, keeping only steps whose target
  // actually exists for this user's role (the sidebar differs per role) —
  // rather than hard-coding a per-role list here that would drift from
  // Sidebar.tsx's own role logic.
  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY) === TOUR_VERSION) return;
    const available = ALL_STEPS.filter(
      (step) => !step.selector || document.querySelector(step.selector)
    );
    if (available.length > 1) setSteps(available);
  }, []);

  const currentStep = steps?.[stepIndex];

  const recomputeRect = () => {
    if (!currentStep?.selector) {
      setRect(null);
      return;
    }
    const el = document.querySelector(currentStep.selector);
    if (!el) {
      setRect(null);
      return;
    }
    const r = el.getBoundingClientRect();
    setRect({
      top: r.top - PADDING,
      left: r.left - PADDING,
      width: r.width + PADDING * 2,
      height: r.height + PADDING * 2,
    });
  };

  useLayoutEffect(() => {
    if (!currentStep?.selector) {
      setRect(null);
      return;
    }
    const el = document.querySelector(currentStep.selector);
    if (!el) {
      setRect(null);
      return;
    }
    // Sidebar items near the bottom of a long, role-specific menu sit inside
    // its own overflow-y-auto scroll area — without this, a target below
    // that inner fold measures a rect the tooltip math places off-screen,
    // and since the tooltip is position:fixed, scrolling the page (which
    // doesn't scroll a fixed element anyway) can never bring it back.
    el.scrollIntoView({ block: 'center', inline: 'nearest' });
    recomputeRect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex, steps]);

  useEffect(() => {
    if (!steps) return;
    window.addEventListener('resize', recomputeRect);
    window.addEventListener('scroll', recomputeRect, true);
    return () => {
      window.removeEventListener('resize', recomputeRect);
      window.removeEventListener('scroll', recomputeRect, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps, stepIndex]);

  const finish = () => {
    localStorage.setItem(STORAGE_KEY, TOUR_VERSION);
    setSteps(null);
  };

  if (!steps || !currentStep) return null;

  const isLast = stepIndex === steps.length - 1;
  const isFirst = stepIndex === 0;

  // Tooltip placement: prefer below the target, flip above if there isn't
  // room — but always as a plain `top` clamped inside the viewport (never
  // `bottom`-anchored growing upward off-screen, and never past
  // window.innerHeight). A position:fixed box that ends up beyond the
  // viewport can't be scrolled to — there's nothing to scroll, since fixed
  // elements don't move with page scroll — so without this clamp, a target
  // near the bottom of the screen (or a short/resized window) could push
  // the Next button permanently out of reach.
  const tooltipWidth = 320;
  const tooltipMaxHeight = Math.min(260, window.innerHeight - 24);
  let tooltipStyle: React.CSSProperties;
  if (!rect) {
    tooltipStyle = {
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: tooltipWidth,
      maxHeight: tooltipMaxHeight,
      overflowY: 'auto',
    };
  } else {
    const spaceBelow = window.innerHeight - (rect.top + rect.height);
    const spaceAbove = rect.top;
    const placeBelow = spaceBelow >= tooltipMaxHeight + 12 || spaceBelow >= spaceAbove;
    const rawTop = placeBelow ? rect.top + rect.height + 12 : rect.top - 12 - tooltipMaxHeight;
    const top = Math.min(Math.max(rawTop, 12), window.innerHeight - tooltipMaxHeight - 12);
    const left = Math.min(
      Math.max(12, rect.left + rect.width / 2 - tooltipWidth / 2),
      window.innerWidth - tooltipWidth - 12
    );
    tooltipStyle = { top, left, width: tooltipWidth, maxHeight: tooltipMaxHeight, overflowY: 'auto' };
  }

  return (
    <>
      {/* Blocks interaction with the rest of the page while the tour is open */}
      <div className="fixed inset-0 z-[9997]" onClick={(e) => e.stopPropagation()} />

      {rect && (
        <div
          className="fixed z-[9998] rounded-lg transition-all duration-300 pointer-events-none"
          style={{
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.6)',
          }}
        />
      )}
      {!rect && (
        <div className="fixed inset-0 z-[9998] bg-slate-900/60" />
      )}

      <div
        className="fixed z-[9999] bg-white rounded-xl shadow-2xl p-5"
        style={tooltipStyle}
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <h3 className="font-bold text-gray-900">{currentStep.title}</h3>
          <button onClick={finish} className="p-0.5 hover:bg-gray-100 rounded shrink-0" aria-label="Skip tour">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>
        <p className="text-sm text-gray-600 mb-4">{currentStep.description}</p>

        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400 tabular-nums">
            {stepIndex + 1} of {steps.length}
          </span>
          <div className="flex items-center gap-2">
            {!isFirst && (
              <button
                onClick={() => setStepIndex((i) => i - 1)}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Back
              </button>
            )}
            {isLast ? (
              <button
                onClick={finish}
                className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700"
              >
                Done
              </button>
            ) : (
              <button
                onClick={() => setStepIndex((i) => i + 1)}
                className="flex items-center gap-1 px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700"
              >
                Next
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default GuidedTour;
